import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { loadErrorMessage } from "shared/loadOrError";
import { canUseDataMapper } from "../rbac";
import { dataMapperSelection, dataMapperService } from "../dataMapperServices";
import {
  isRecordChanged,
  sameId,
  visibleToSelection,
  type DataMapperScope,
  type DataMapperSchemaEntry,
} from "../dataMapper.types";
import { parseTypeBoxSchema } from "../engine/typeboxParser";
import type { ParsedSchema } from "../engine/types";
import { bindSelectionToSignedInUser, editorFailure, groupByScope } from "../shared/editorSupport";
import type DataMapperProfileStateStore from "../DataMapperProfileStateStore";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

/** How a save ended. `conflict` = 409 record-changed: the form closes and the list reloads. */
export type SchemaSaveOutcome = "saved" | "conflict" | "failed";

export interface SchemaParseResult {
  schema: ParsedSchema | null;
  error: string | null;
}

/** A stored schema value, if it is the parsed shape the editors read. */
export const asParsedSchema = (value: unknown): ParsedSchema | null => {
  const v = value as Partial<ParsedSchema> | null;
  return v && typeof v === "object" && typeof v.name === "string" && Array.isArray(v.fields)
    ? (v as ParsedSchema)
    : null;
};

export const schemaRecordChangedNotice = (name: string): string =>
  `Someone else saved "${name}" while you were editing it, so your changes were not saved. ` +
  "The list has been reloaded with their version; open the schema again to apply yours.";

/**
 * Data Mapper → Schema: the target schemas the open profile sees (its own, its
 * client's, global), and an editor that takes pasted TypeBox code, parses it
 * WITHOUT evaluating it (engine/typeboxParser.ts) and saves the parsed fields
 * as JSON (`data-mapper-schemas`, `schema` = the parsed schema with its
 * rawCode, as the standalone tool saved it).
 *
 * Plain MobX class (no ViewStore base). The rows stay in the ServiceStore;
 * this holds the form, and the chosen schema is an id on DataMapperViewStore.
 */
class DataMapperSchemasStore {
  rootStore: RootStore;

  loading = false;
  loaded = false;
  error: string | null = null;
  actionError: string | null = null;
  notice: string | null = null;

  // ---- The editor form ----
  /** True while the editor is open (create or edit). */
  formOpen = false;
  editingSchemaId: string | null = null;
  /** The `updatedAt` the edit was loaded from — sent back as `expectUpdatedAt`. */
  editingUpdatedAt: string | null = null;
  formName = "";
  formDescription = "";
  formScope: DataMapperScope = "profile";
  formCode = "";
  saving = false;
  formError: string | null = null;

  private loadSeq = 0;
  /** Bumped when the form is reset by a route exit: a save answering after that touches nothing. */
  private formEpoch = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private get service() {
    return dataMapperService(this.rootStore, "data-mapper-schemas");
  }

  private get selection() {
    return dataMapperSelection(this.rootStore);
  }

  /** The Map/Transform draft, which belongs to the chosen schema. */
  private get mappingStore(): { dirty: boolean; schemaChanged: () => void } | null {
    return (
      (this.rootStore as unknown as { dataMapperMappingViewStore?: { dirty: boolean; schemaChanged: () => void } })
        .dataMapperMappingViewStore ?? null
    );
  }

  /** Choosing `id` would discard unsaved mapping edits made for the current schema (the screen asks first). */
  wouldDiscardMappingEdits(id: string | null): boolean {
    return !sameId(id, this.selectedSchemaId) && this.mappingStore?.dirty === true;
  }

  private get stateStore(): DataMapperProfileStateStore | null {
    return (
      (this.rootStore as unknown as { dataMapperProfileStateViewStore?: DataMapperProfileStateStore })
        .dataMapperProfileStateViewStore ?? null
    );
  }

  get canUse(): boolean {
    return canUseDataMapper(this.rootStore);
  }

  get profileId(): string | null {
    return this.selection?.selectedProfileId ?? null;
  }

  get clientId(): string | null {
    return this.selection?.selectedClientId ?? null;
  }

  get selectedSchemaId(): string | null {
    return this.selection?.selectedSchemaId ?? null;
  }

  /** The schemas the open profile sees — a computed view over the ServiceStore cache. */
  get schemas(): DataMapperSchemaEntry[] {
    if (!this.profileId) return [];
    const rows = (this.service?.entities ?? []) as unknown as DataMapperSchemaEntry[];
    return rows.filter((r) => visibleToSelection(r, this.clientId, this.profileId));
  }

  get groups() {
    return groupByScope(this.schemas);
  }

  schemaById(id: string | null): DataMapperSchemaEntry | null {
    if (!id) return null;
    return this.schemas.find((s) => sameId(s.id, id)) ?? null;
  }

  get selectedSchema(): DataMapperSchemaEntry | null {
    return this.schemaById(this.selectedSchemaId);
  }

  get editingSchema(): DataMapperSchemaEntry | null {
    return this.schemaById(this.editingSchemaId);
  }

  /**
   * The form's schema: the pasted code, parsed (never evaluated). An edit whose
   * code box is empty keeps the stored schema (one built without code has no
   * rawCode to show).
   */
  get parseResult(): SchemaParseResult {
    const code = this.formCode;
    if (!code.trim()) {
      const stored = this.editingSchema ? asParsedSchema(this.editingSchema.schema) : null;
      return { schema: stored, error: null };
    }
    try {
      return { schema: parseTypeBoxSchema(code), error: null };
    } catch (err) {
      return { schema: null, error: loadErrorMessage(err, "The TypeBox code could not be read.") };
    }
  }

  get isFormValid(): boolean {
    return this.formName.trim().length > 0 && this.parseResult.schema !== null && !this.saving;
  }

  // ---- Loading -------------------------------------------------------------

  /** The open profile's schemas. Every find names the selection. */
  async load(): Promise<void> {
    const seq = ++this.loadSeq;
    bindSelectionToSignedInUser(this.rootStore);
    const selection = this.selection;
    if (!this.canUse || !selection?.hasProfile) {
      this.loading = false;
      this.loaded = false;
      this.error = null;
      return;
    }
    const query = { ...selection.selectionQuery };
    this.loading = true;
    this.error = null;
    // The reset after a sign-out bumps loadSeq, but the epoch moves first: a
    // find ended in between would still pass the seq check and latch `loaded`.
    const ended = sessionMark(this.rootStore);
    try {
      if (!this.service) throw new Error("The Data Mapper service is not available in this build.");
      await this.service.find({ query });
      // A chosen schema that is gone (deleted elsewhere) is dropped.
      runInAction(() => {
        if (seq !== this.loadSeq) return;
        const chosen = this.selectedSchemaId;
        if (chosen && !this.schemaById(chosen)) selection.forgetSchema(chosen);
      });
    } catch (err) {
      runInAction(() => {
        if (seq === this.loadSeq)
          { if (!isSessionEnded(err)) this.error = editorFailure(this.rootStore, err, query.profileId ?? null, "The schemas could not be loaded."); }
      });
    } finally {
      runInAction(() => {
        if (seq === this.loadSeq && !ended()) {
          this.loading = false;
          this.loaded = true;
        }
      });
    }
  }

  // ---- Choosing ------------------------------------------------------------

  /** Choose the schema the Map screen maps onto (an id on the selection; saved to the profile). */
  chooseSchema(id: string | null) {
    if (this.saving) return;
    this.selection?.selectSchema(id);
    // The mapping draft made for the old schema closes now (Codex r2).
    this.mappingStore?.schemaChanged();
    this.stateStore?.schedule();
  }

  // ---- The form ------------------------------------------------------------

  startCreate() {
    if (this.saving) return;
    this.formOpen = true;
    this.editingSchemaId = null;
    this.editingUpdatedAt = null;
    this.formName = "";
    this.formDescription = "";
    this.formScope = "profile";
    this.formCode = "";
    this.formError = null;
  }

  startEdit(entry: DataMapperSchemaEntry) {
    if (this.saving) return;
    const parsed = asParsedSchema(entry.schema);
    this.formOpen = true;
    this.editingSchemaId = entry.id;
    this.editingUpdatedAt = entry.updatedAt;
    this.formName = entry.name;
    this.formDescription = entry.description ?? "";
    this.formScope = entry.scope;
    this.formCode = parsed?.rawCode ?? "";
    this.formError = null;
  }

  closeForm() {
    this.formOpen = false;
    this.editingSchemaId = null;
    this.editingUpdatedAt = null;
    this.formError = null;
  }

  setFormName(v: string) {
    if (this.saving) return; // frozen while a save is in flight: its answer closes the form (Codex r1)
    this.formName = v;
  }

  setFormDescription(v: string) {
    if (this.saving) return; // frozen while a save is in flight: its answer closes the form (Codex r1)
    this.formDescription = v;
  }

  setFormScope(v: DataMapperScope) {
    if (this.saving) return; // frozen while a save is in flight: its answer closes the form (Codex r1)
    this.formScope = v;
  }

  setFormCode(v: string) {
    if (this.saving) return; // frozen while a save is in flight: its answer closes the form (Codex r1)
    this.formCode = v;
  }

  clearNotice() {
    this.notice = null;
  }

  clearActionError() {
    this.actionError = null;
  }

  /**
   * Create or edit. An edit names the selection (a by-id call outside it is
   * not found) and sends the `updatedAt` it was loaded from, so a concurrent
   * save is a 409 `record-changed`: the form closes, the list reloads with the
   * other person's version and a sentence says what happened.
   */
  async save(): Promise<SchemaSaveOutcome> {
    const parsed = this.parseResult.schema;
    if (!this.formName.trim() || !parsed) {
      this.formError = this.parseResult.error ?? "A schema needs a name and TypeBox code with a Type.Object.";
      return "failed";
    }
    const service = this.service;
    const selection = this.selection;
    if (!service || !selection?.hasProfile) {
      this.formError = "Open a profile first.";
      return "failed";
    }
    const query = { ...selection.selectionQuery };
    const name = this.formName.trim();
    const description = this.formDescription.trim() || null;
    // Saved as JSON: the parsed fields, with the code they came from.
    const schema: ParsedSchema = { ...parsed, rawCode: this.formCode.trim() ? this.formCode : parsed.rawCode };
    const editingId = this.editingSchemaId;
    // The answer belongs to this form and this profile; the tab may have moved on (Codex r2).
    const epoch = this.formEpoch;
    const stale = () => epoch !== this.formEpoch || !sameId(this.profileId, query.profileId);
    this.saving = true;
    this.formError = null;
    try {
      let savedId: string;
      if (editingId) {
        const patch: Record<string, unknown> = { name, description, schema };
        if (this.editingUpdatedAt) patch.expectUpdatedAt = this.editingUpdatedAt;
        const saved = (await service.patch(editingId, patch as never, { query })) as unknown as DataMapperSchemaEntry;
        savedId = saved?.id ?? editingId;
      } else {
        const saved = (await service.create({ name, description, schema, scope: this.formScope, ...query } as never)) as unknown as DataMapperSchemaEntry;
        savedId = saved.id;
      }
      if (stale()) return "saved";
      runInAction(() => {
        this.closeForm();
        this.notice = null;
        this.saving = false;
      });
      // Choose the saved schema, unless that would silently discard mapping edits for another.
      if (!this.wouldDiscardMappingEdits(savedId)) this.chooseSchema(savedId);
      await this.load();
      return "saved";
    } catch (err) {
      if (stale() || isSessionEnded(err)) return "failed";
      if (isRecordChanged(err)) {
        runInAction(() => {
          this.notice = schemaRecordChangedNotice(name);
          this.closeForm();
        });
        await this.load();
        return "conflict";
      }
      runInAction(() => {
        this.formError = editorFailure(this.rootStore, err, query.profileId ?? null, "The schema could not be saved.");
      });
      return "failed";
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  /** Delete a schema (the caller confirms first). Mapping configs keep their own copy of it. */
  async remove(entry: DataMapperSchemaEntry): Promise<boolean> {
    if (this.saving) return false;
    this.actionError = null;
    this.notice = null;
    const query = { ...(this.selection?.selectionQuery ?? {}) };
    try {
      if (!this.service) throw new Error("The Data Mapper service is not available in this build.");
      await this.service.remove(entry.id, { query });
      this.selection?.forgetSchema(entry.id);
      this.mappingStore?.schemaChanged();
      if (sameId(this.editingSchemaId, entry.id)) this.closeForm();
      this.stateStore?.schedule();
      await this.load();
      return true;
    } catch (err) {
      // Ended by a sign-out: no error, and no re-read in the next session.
      if (isSessionEnded(err)) return false;
      runInAction(() => {
        this.actionError = editorFailure(this.rootStore, err, query.profileId ?? null, `"${entry.name}" could not be deleted.`);
      });
      await this.load();
      return false;
    }
  }

  /** Route exit: drop the transient form and messages (the selection stays). */
  reset() {
    this.loadSeq++;
    this.formEpoch++;
    this.loading = false;
    this.error = null;
    this.actionError = null;
    this.notice = null;
    this.saving = false;
    this.closeForm();
  }

  /**
   * Everything reset() keeps across this wizard's route changes goes too: on
   * sign-out it was the previous person's. Called by RootStore.purgeUserData,
   * which prefers this to reset().
   */
  resetForSignOut() {
    this.reset();
    this.loaded = false;
    this.formName = "";
    this.formDescription = "";
    this.formScope = "profile";
    this.formCode = "";
  }
}

export default DataMapperSchemasStore;
